import { CalendarService } from '@/class/services/CalendarService'
import { ISchedule, Schedule } from '@/components/Schedule/Schedule'
import AppState from '@/states/app'
import { reactive } from 'vue'

interface UserScheduleInfo {
  scheduleId: string | null
  hasDateSpecificHours: boolean
  isDefault: boolean
  scheduleName: string
}

interface CalendarScheduleState {
  userSchedulesInfo: Record<string, UserScheduleInfo>
  schedules: Record<string, Schedule[]>
  eventCalendarSchedule: Record<string, Schedule>
  loading: boolean
  initialLoad: boolean
}

const state = reactive<CalendarScheduleState>({
  userSchedulesInfo: {},
  schedules: {},
  eventCalendarSchedule: {},
  loading: false,
  initialLoad: false,
})

const fetchSchedulesForUsers = async (
  userIds: string[],
  calendarId: string | null = null,
  forceRefresh = false
) => {
  state.loading = true
  if (userIds.length === 0) {
    //load default schedules and isCustom schedules for all users
    // Only fetch calendar-specific schedules if calendarId is provided
    const requests = [
      CalendarService.getCalendarSchedules({
        locationId: AppState.locationId,
        isDefault: true,
      }),
    ]

    if (calendarId) {
      requests.push(
        CalendarService.getCalendarSchedules({
          locationId: AppState.locationId,
          calendarId: calendarId,
        }),
        CalendarService.getCalendarSchedules({
          locationId: AppState.locationId,
          calendarId: calendarId,
          isCustom: true,
        })
      )
    }

    const responses = await Promise.all(requests)
    const defaultSchedules = responses[0]?.data ?? []
    const calendarSchedulesData = responses[1]?.data ?? []
    const customSchedules = responses[2]?.data ?? []
    const schedulesData = [
      ...defaultSchedules,
      ...calendarSchedulesData,
      ...customSchedules,
    ]
    //put user's in a record with key userId and value an array of schedules
    schedulesData.forEach(s => {
      if (!state.schedules[s.userId]) {
        state.schedules[s.userId] = []
      }
      //check if the schedule is already in the array then update it
      const index = state.schedules[s.userId].findIndex(x => x.id === s.id)
      if (index !== -1) {
        state.schedules[s.userId][index] = new Schedule(s)
      } else {
        state.schedules[s.userId].push(new Schedule(s))
      }
    })
    //update userSchedulesInfo for each user
    Object.keys(state.schedules).forEach(userId => {
      updateUserScheduleInfo(userId, calendarId)
    })
    state.loading = false
    return
  }
  const promises = userIds.map(async userId => {
    if (!forceRefresh && state.schedules[userId]) {
      // Data already exists, just re-evaluate userSchedulesInfo
      updateUserScheduleInfo(userId, calendarId)
      return
    }

    try {
      // If calendarId is null/undefined (draft calendar), only fetch user's default schedules
      // Don't try to fetch custom schedules for a calendar that doesn't exist yet
      const requests = [
        CalendarService.getCalendarSchedules({
          locationId: AppState.locationId,
          userId,
        }),
      ]

      // Only fetch custom calendar schedules if calendar exists (has ID)
      if (calendarId) {
        requests.push(
          CalendarService.getCalendarSchedules({
            locationId: AppState.locationId,
            userId,
            isCustom: true,
            calendarId,
          })
        )
      }

      const responses = await Promise.all(requests)
      const response = responses[0]
      const responseCustom = responses[1]

      const data1 = response?.data ?? []
      const dataCustom = responseCustom?.data ?? []
      const schedulesData = [...data1, ...dataCustom]

      state.schedules[userId] = schedulesData.map(s => new Schedule(s))

      updateUserScheduleInfo(userId, calendarId)
    } catch (error) {
      console.error(`Failed to fetch schedules for user ${userId}:`, error)
    }
  })

  await Promise.all(promises)
  state.loading = false
}

const updateUserScheduleInfo = (userId: string, calendarId: string | null) => {
  const userSchedules = state.schedules[userId]
  if (!userSchedules) {
    return
  }

  let associatedSchedule: Schedule | undefined

  if (calendarId) {
    // Find custom schedule for this calendar
    associatedSchedule = userSchedules.find(s =>
      s.calendarIds.includes(calendarId)
    )
    // Find schedule associated with this calendar
    if (!associatedSchedule) {
      associatedSchedule = userSchedules.find(s =>
        s.calendarIds.includes(calendarId)
      )
    }
  }

  // If no calendar-specific schedule, find the default one
  if (!associatedSchedule) {
    associatedSchedule = userSchedules.find(s => s.isDefault)
  }

  if (associatedSchedule) {
    state.userSchedulesInfo[userId] = {
      scheduleId: associatedSchedule.id,
      hasDateSpecificHours:
        associatedSchedule.getDateSpecificHours().length > 0,
      isDefault: associatedSchedule.isDefault,
      scheduleName: associatedSchedule.name,
    }
  } else {
    state.userSchedulesInfo[userId] = {
      scheduleId: null,
      hasDateSpecificHours: false,
      isDefault: false,
      scheduleName: 'No schedule associated',
    }
  }
}

const createSchedule = async (scheduleData: Partial<ISchedule>) => {
  const payload = {
    rules: scheduleData.rules,
    name: scheduleData.name,
    timezone: scheduleData.timezone,
    calendarIds: scheduleData.calendarIds,
    locationId: AppState.locationId,
    isCustom: scheduleData.isCustom,
    userId: scheduleData.userId,
  }
  const result = await CalendarService.createSchedule(payload)
  const newSchedule = new Schedule({
    ...result.data,
    isCreated: true,
  })

  // For EVENT calendars (no userId), store in eventCalendarSchedule instead
  if (!newSchedule.userId && scheduleData.calendarIds?.[0]) {
    state.eventCalendarSchedule[scheduleData.calendarIds[0]] = newSchedule
    return newSchedule
  }

  // For user-specific schedules (Round Robin, Collective, etc.)
  if (state.schedules[newSchedule.userId]) {
    //check if the schedule is already in the array then update it
    //if newSchedule is custom schedule, then remove other custom schedule for this user
    if (newSchedule.isCustom) {
      state.schedules[newSchedule.userId] = state.schedules[
        newSchedule.userId
      ].filter(s => !s.isCustom)
    }
    const index = state.schedules[newSchedule.userId].findIndex(
      s => s.id === newSchedule.id
    )
    if (index !== -1) {
      state.schedules[newSchedule.userId][index] = newSchedule
    } else {
      state.schedules[newSchedule.userId].push(newSchedule)
    }
  } else {
    state.schedules[newSchedule.userId] = [newSchedule]
  }

  updateUserScheduleInfo(
    newSchedule.userId,
    scheduleData.calendarIds ? scheduleData.calendarIds[0] : null
  )
  return newSchedule
}

const updateSchedule = async (schedule: Schedule) => {
  const { rules, name, timezone, calendarIds } = schedule.toJSON()
  const result = await CalendarService.updateCalendarSchedule(schedule.id, {
    rules,
    name,
    timezone,
    calendarIds,
  })

  const updatedSchedule = new Schedule(result.data)

  if (!state.schedules[schedule.userId]) {
    state.schedules[schedule.userId] = []
  }
  const index = state.schedules[schedule.userId].findIndex(
    s => s.id === schedule.id
  )
  if (index !== -1) {
    state.schedules[schedule.userId][index] = updatedSchedule
  } else {
    state.schedules[schedule.userId].push(updatedSchedule)
  }
  updateUserScheduleInfo(schedule.userId, calendarIds ? calendarIds[0] : null)
  return updatedSchedule
}

const clearState = () => {
  state.userSchedulesInfo = {}
  state.schedules = {}
  state.loading = false
  state.eventCalendarSchedule = {}
  state.initialLoad = false
}

const getEventCalendarSchedules = async (
  calendarId: string
): Promise<Schedule> => {
  // If calendarId is null/undefined (draft calendar), return empty schedule
  if (!calendarId) {
    return new Schedule({})
  }

  if (state.eventCalendarSchedule[calendarId]) {
    return state.eventCalendarSchedule[calendarId]
  }
  const result = await CalendarService.getCalendarSchedules({
    locationId: AppState.locationId,
    calendarId,
    isCustom: true,
  })
  const schedules = result?.data ?? []
  const scheduleData = schedules.find((s: any) =>
    s.calendarIds?.includes(calendarId)
  )
  if (!scheduleData) {
    return new Schedule({})
  }
  state.eventCalendarSchedule[calendarId] = new Schedule(scheduleData)
  return state.eventCalendarSchedule[calendarId]
}

export const calendarScheduleState = {
  state,
  fetchSchedulesForUsers,
  updateUserScheduleInfo,
  createSchedule,
  updateSchedule,
  clearState,
  getEventCalendarSchedules,
}

export default calendarScheduleState
